import { useMemo, useState, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { CheckCircle2, AlertCircle, MessageCircle, Phone, Send, Info } from "lucide-react";
import { z } from "zod";
import { SITE } from "@/config/site";
import {
  ADDONS,
  CARPET_PRICES,
  SERVICES,
  carpetSelectionLabel,
  type CarpetSelection,
  calculateQuote,
  formatPrice,
  formatRange,
  summariseForWhatsApp,
  type AddonId,
  type Bathrooms,
  type Bedrooms,
  type Condition,
  type Frequency,
  type KitchenSize,
  type QuoteInput,
  type Receptions,
  type ServiceId,
} from "@/lib/quote-pricing";
import { sendQuoteToWhatsApp } from "@/lib/whatsapp-quote.functions";
import { createBookingFromQuote } from "@/lib/bookings.functions";
import { Input } from "@/components/ui/input";

const UK_POSTCODE_RE = /^([A-Z]{1,2}\d[A-Z\d]?)\s*(\d[A-Z]{2})$/i;
const UK_PHONE_RE = /^(?:\+44\s?|0)(?:\d\s?){9,10}\d$/;

const contactSchema = z.object({
  name: z.string().trim().min(2, "Please enter your full name").max(80),
  email: z.string().trim().toLowerCase().email("Enter a valid email address").max(120),
  phone: z.string().trim().max(20).regex(UK_PHONE_RE, "Enter a valid UK phone number"),
  postcode: z.string().trim().toUpperCase().regex(UK_POSTCODE_RE, "Enter a valid UK postcode"),
  address: z.string().trim().max(200).optional(),
  date: z.string().optional(),
  notes: z.string().trim().max(1500).optional(),
  consent: z.literal("on", { message: "Please agree so we can contact you" }),
});

const bedroomsOpts: Bedrooms[] = ["studio", "1", "2", "3", "4", "5+"];
const bathroomsOpts: Bathrooms[] = ["1", "2", "3", "4+"];
const receptionsOpts: Receptions[] = ["0", "1", "2", "3+"];
const kitchenOpts: KitchenSize[] = ["small", "medium", "large"];
const conditionOpts: { id: Condition; label: string }[] = [
  { id: "light", label: "Light" },
  { id: "standard", label: "Standard" },
  { id: "heavy", label: "Heavy" },
  { id: "very-heavy", label: "Very heavy" },
];
const frequencyOpts: { id: Frequency; label: string }[] = [
  { id: "one-off", label: "One-off" },
  { id: "weekly", label: "Weekly" },
  { id: "fortnightly", label: "Fortnightly" },
  { id: "monthly", label: "Monthly" },
];

const propertyTypeOpts = ["Flat", "Bungalow", "House", "Office", "Other"] as const;
type PropertyType = (typeof propertyTypeOpts)[number];

function frequencyForService(s: ServiceId): Frequency {
  if (s === "airbnb") return "one-off";
  if (s === "one-off" || s === "deep" || s === "tenancy" || s === "military") return "one-off";
  return "weekly";
}

export interface QuoteInitial {
  service?: ServiceId;
  propertyType?: PropertyType;
  bedrooms?: Bedrooms;
  bathrooms?: Bathrooms;
  receptions?: Receptions;
  kitchen?: KitchenSize;
  condition?: Condition;
  frequency?: Frequency;
  furnished?: "furnished" | "unfurnished" | "partial";
  pets?: "yes" | "no";
  parking?: "yes" | "no";
  addons?: AddonId[];
  postcode?: string;
  date?: string;
}

export function InstantQuoteCalculator({
  initialService,
  initial,
  prefilled,
}: {
  initialService?: ServiceId;
  initial?: QuoteInitial;
  prefilled?: boolean;
}) {
  const seed: QuoteInitial = initial ?? { service: initialService };
  const [service, setService] = useState<ServiceId>(seed.service ?? "regular");
  const [propertyType, setPropertyType] = useState<PropertyType>(seed.propertyType ?? "House");
  const [bedrooms, setBedrooms] = useState<Bedrooms>(seed.bedrooms ?? "2");
  const [bathrooms, setBathrooms] = useState<Bathrooms>(seed.bathrooms ?? "1");
  const [receptions, setReceptions] = useState<Receptions>(seed.receptions ?? "1");
  const [kitchen, setKitchen] = useState<KitchenSize>(seed.kitchen ?? "medium");
  const [condition, setCondition] = useState<Condition>(seed.condition ?? "standard");
  const [frequency, setFrequency] = useState<Frequency>(() =>
    seed.frequency === "one-off" ||
    seed.frequency === "weekly" ||
    seed.frequency === "fortnightly" ||
    seed.frequency === "monthly"
      ? seed.frequency
      : frequencyForService(seed.service ?? "regular"),
  );
  const [furnished, setFurnished] = useState<"furnished" | "unfurnished" | "partial">(seed.furnished ?? "furnished");
  const [pets, setPets] = useState<"yes" | "no">(seed.pets ?? "no");
  const [parking, setParking] = useState<"yes" | "no">(seed.parking ?? "yes");
  const [addons, setAddons] = useState<AddonId[]>(() =>
    seed.service === "tenancy" || seed.service === "military"
      ? (seed.addons ?? []).filter((id) => id !== "cupboards")
      : (seed.addons ?? []),
  );
  const [carpetRooms, setCarpetRooms] = useState<CarpetSelection>({});
  const [wheelieBins, setWheelieBins] = useState(1);

  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const navigate = useNavigate();

  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const input: QuoteInput = {
    service,
    bedrooms,
    bathrooms,
    receptions,
    kitchen,
    condition,
    frequency,
    addons,
    carpetRooms,
    wheelieBins,
  };
  const result = useMemo(() => calculateQuote(input), [
    service, bedrooms, bathrooms, receptions, kitchen, condition, frequency, addons, carpetRooms, wheelieBins,
  ]);

  function toggleAddon(id: AddonId) {
    setAddons((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  }

  function setCarpetQty(id: string, qty: number) {
    setCarpetRooms((prev) => {
      const next = { ...prev };
      if (qty <= 0) delete next[id];
      else next[id] = Math.min(qty, 20);
      return next;
    });
  }

  function pickService(id: ServiceId) {
    setService(id);
    setFrequency(frequencyForService(id));
    if (id === "tenancy" || id === "military") {
      setAddons((prev) => prev.filter((addon) => addon !== "cupboards"));
    }
  }

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setErrorMsg("");
    setFieldErrors({});

    const form = e.currentTarget;
    const fd = new FormData(form);
    const raw = Object.fromEntries(fd.entries()) as Record<string, string>;
    if (raw.botcheck) { setStatus("sent"); return; }

    const parsed = contactSchema.safeParse(raw);
    if (!parsed.success) {
      const flat: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as string;
        if (k && !flat[k]) flat[k] = issue.message;
      }
      setFieldErrors(flat);
      setStatus("error");
      setErrorMsg("Please fix the highlighted fields.");
      return;
    }
    const v = parsed.data;

    setStatus("sending");

    const serviceLabel = SERVICES.find((s) => s.id === service)?.label ?? service;
    const estimateLine =
      result.mode === "custom"
        ? "Custom commercial quote required"
        : formatRange(result);

    if (SITE.web3formsAccessKey) {
      try {
        const payload: Record<string, string> = {
          access_key: SITE.web3formsAccessKey,
          subject: `Instant quote request — ${v.name} — ${serviceLabel}`,
          from_name: "IZHUB Instant Quote",
          email: v.email,
          replyto: v.email,
          cc: SITE.replyToEmail,
          Name: v.name,
          Phone: v.phone,
          Postcode: v.postcode,
          Address: v.address || "—",
          "Preferred date": v.date || "—",
          Service: serviceLabel,
          "Property type": propertyType,
          Bedrooms: bedrooms,
          Bathrooms: bathrooms,
          "Reception rooms": receptions,
          "Kitchen size": kitchen,
          Condition: condition,
          Frequency: frequency,
          Furnished: furnished,
          Pets: pets,
          Parking: parking,
          "Add-ons": addons.length
            ? addons.map((a) => ADDONS.find((x) => x.id === a)?.label ?? a).join(", ")
            : "—",
          "Carpet rooms": carpetSelectionLabel(carpetRooms) || "—",
          "Wheelie bins": addons.includes("wheelie-bins") ? String(wheelieBins) : "—",
          "Instant estimate": estimateLine,
          "Price to confirm": result.priceToConfirm.join(", ") || "—",
          Notes: v.notes || "—",
        };
        const res = await fetch("https://api.web3forms.com/submit", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.success) throw new Error(data.message || `Request failed (${res.status})`);
        // Automatic WhatsApp delivery — fire-and-forget so the customer
        // proceeds to the thank-you page without waiting; failures are logged
        // and never block the email confirmation or the redirect.
        const whatsappDetails: [string, string][] = [
          ["Address", v.address || "—"],
          ["Preferred date", v.date || "—"],
          ["Property type", propertyType],
          ["Bedrooms", bedrooms],
          ["Bathrooms", bathrooms],
          ["Reception rooms", receptions],
          ["Kitchen size", kitchen],
          ["Condition", condition],
          ["Frequency", frequency],
          ["Furnished", furnished],
          ["Pets", pets],
          ["Parking", parking],
          [
            "Add-ons",
            addons.length
              ? addons.map((a) => ADDONS.find((x) => x.id === a)?.label ?? a).join(", ")
              : "—",
          ],
          ["Carpet rooms", carpetSelectionLabel(carpetRooms) || "—"],
          ["Wheelie bins", addons.includes("wheelie-bins") ? String(wheelieBins) : "—"],
        ];
        sendQuoteToWhatsApp({
          data: {
            name: v.name,
            phone: v.phone,
            email: v.email,
            postcode: v.postcode,
            service: serviceLabel,
            details: whatsappDetails,
            estimate: estimateLine,
            notes: v.notes || undefined,
          },
        }).catch((err) => console.warn("[quote] WhatsApp delivery failed", err));
        // Draft booking confirmation for the admin portal.
        createBookingFromQuote({
          data: {
            name: v.name,
            email: v.email,
            phone: v.phone,
            postcode: v.postcode,
            address: v.address || "",
            service: serviceLabel,
            details: whatsappDetails.map(([k, val]) => `${k}: ${val}`).join("\n") + (v.notes ? `\nNotes: ${v.notes}` : ""),
            preferredDate: v.date || "",
            estimate: estimateLine,
            suggestedPrice: result.mode === "custom" || !result.high ? null : result.high,
          },
        }).catch((err) => console.warn("[quote] booking draft failed", err));
        console.log("[quote] WhatsApp delivery dispatched");
        // Mark this navigation as a genuine successful submission so /thank-you can fire conversions.
        try {
          sessionStorage.setItem("quote_submission_success", "true");
          console.log("[quote] set quote_submission_success flag, navigating to /thank-you");
        } catch (err) {
          console.warn("[quote] sessionStorage set failed", err);
        }
        navigate({ to: "/thank-you" });
        return;

      } catch {
        setErrorMsg("Sorry, something went wrong. Please try again or WhatsApp us.");
        setStatus("error");
        return;
      }
    }

    // mailto fallback
    const body = [
      `Name: ${v.name}`,
      `Phone: ${v.phone}`,
      `Email: ${v.email}`,
      `Postcode: ${v.postcode}`,
      `Address: ${v.address || "—"}`,
      `Preferred date: ${v.date || "—"}`,
      "",
      `Service: ${serviceLabel}`,
      `Property: ${propertyType}, ${bedrooms} bed / ${bathrooms} bath / ${receptions} reception`,
      `Kitchen: ${kitchen}  Condition: ${condition}`,
      `Frequency: ${frequency}  Furnished: ${furnished}  Pets: ${pets}  Parking: ${parking}`,
      `Add-ons: ${addons.map((a) => ADDONS.find((x) => x.id === a)?.label ?? a).join(", ") || "—"}`,
      ...(addons.includes("wheelie-bins") ? [`Wheelie bins: ${wheelieBins}`] : []),
      `Instant estimate: ${estimateLine}`,
      "",
      v.notes || "",
    ].join("\n");
    window.location.href = `mailto:${SITE.contactEmail}?subject=${encodeURIComponent(
      `Instant quote — ${serviceLabel} (${v.postcode})`,
    )}&body=${encodeURIComponent(body)}`;
    setStatus("sent");
  }

  const whatsappHref = `https://wa.me/447869165458?text=${encodeURIComponent(
    summariseForWhatsApp(input, result),
  )}`;

  // Add the "Your details" fields at click time so they always appear in the WhatsApp message.
  function openWhatsAppWithDetails(e: React.MouseEvent<HTMLAnchorElement>) {
    const read = (n: string) =>
      (document.querySelector<HTMLInputElement | HTMLTextAreaElement>(`[name="${n}"]`)?.value ?? "").trim();
    const details = [
      ["Name", read("name")],
      ["Phone", read("phone")],
      ["Email", read("email")],
      ["Postcode", read("postcode")],
      ["Address", read("address")],
      ["Preferred date", read("date")],
      ["Notes", read("notes")],
    ].filter(([, v]) => v);
    const text = [
      summariseForWhatsApp(input, result),
      ...(details.length ? ["", "*Your details*", ...details.map(([k, v]) => `${k}: ${v}`)] : []),
    ].join("\n");
    e.preventDefault();
    window.open(`https://wa.me/447869165458?text=${encodeURIComponent(text)}`, "_blank", "noopener,noreferrer");
  }

  if (status === "sent") {
    return (
      <div className="rounded-3xl border border-sage/40 bg-sage-soft/60 p-8 text-center">
        <CheckCircle2 className="mx-auto h-10 w-10 text-primary" />
        <h3 className="mt-3 font-display text-2xl">Thank you.</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Your quote request has been sent to IZHUB. We usually reply within 2 hours during business hours.
        </p>
        <p className="mt-3 text-sm text-muted-foreground">
          In a hurry? <a className="underline" href={`tel:${SITE.phone}`}>{SITE.phoneDisplay}</a> or{" "}
          <a className="underline" href={SITE.whatsapp} target="_blank" rel="noreferrer">WhatsApp Us</a>.
        </p>
      </div>
    );
  }

  const serviceLabel = SERVICES.find((s) => s.id === service)?.label ?? service;
  const bedroomsLabel = bedrooms === "studio" ? "Studio" : `${bedrooms} bed`;
  const addonLabels = addons
    .map((id) => ADDONS.find((a) => a.id === id)?.label)
    .filter(Boolean) as string[];

  return (
    <div className="grid gap-8 lg:grid-cols-[1.4fr_1fr] items-start">
      {/* LEFT — calculator inputs */}
      <div className="space-y-8">
        {prefilled && (
          <div className="rounded-2xl border border-primary/30 bg-sage-soft/60 p-5 md:p-6">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
              <div className="flex-1 min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-primary/80">Prefilled from your chat</div>
                <p className="mt-1 text-sm text-foreground/85">Please review the details we captured and adjust anything before submitting.</p>
                <dl className="mt-3 grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
                  <PrefillRow k="Service" v={serviceLabel} />
                  <PrefillRow k="Property" v={`${propertyType} · ${bedroomsLabel} · ${bathrooms} bath`} />
                  <PrefillRow k="Rooms" v={`${receptions} reception · ${kitchen} kitchen`} />
                  <PrefillRow k="Condition" v={condition} />
                  <PrefillRow k="Frequency" v={frequency} />
                  {seed.postcode && <PrefillRow k="Postcode" v={seed.postcode} />}
                  {seed.date && <PrefillRow k="Preferred date" v={seed.date} />}
                  {addonLabels.length > 0 && <PrefillRow k="Add-ons" v={addonLabels.join(", ")} />}
                </dl>
              </div>
            </div>
          </div>
        )}
        {/* Step 1: Service */}
        <Section step={1} title="Choose your service">
          <div className="flex flex-wrap gap-2">
            {SERVICES.map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => pickService(s.id)}
                aria-pressed={service === s.id}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  service === s.id
                    ? "border-primary bg-sage-soft text-primary"
                    : "border-border bg-background hover:border-primary"
                }`}
              >
                <span className="font-medium">{s.label}</span>
                <span className="ml-2 text-xs text-muted-foreground hidden sm:inline">{s.blurb}</span>
              </button>
            ))}
          </div>
        </Section>

        {/* Step 2: Property */}
        <Section step={2} title="Property details">
          <div className="grid gap-4 md:grid-cols-2">
            <LabeledSelect label="Property type" value={propertyType} onChange={(v) => setPropertyType(v as PropertyType)} options={propertyTypeOpts as readonly string[]} />
            <LabeledSelect label="Bedrooms" value={bedrooms} onChange={(v) => setBedrooms(v as Bedrooms)} options={bedroomsOpts.map((v) => v === "studio" ? "Studio" : v)} valueMap={bedroomsOpts} />
            <LabeledSelect label="Bathrooms" value={bathrooms} onChange={(v) => setBathrooms(v as Bathrooms)} options={bathroomsOpts} />
            <LabeledSelect label="Reception / living rooms" value={receptions} onChange={(v) => setReceptions(v as Receptions)} options={receptionsOpts} />
            <LabeledSelect label="Kitchen size" value={kitchen} onChange={(v) => setKitchen(v as KitchenSize)} options={kitchenOpts} labelize />
            <LabeledSelect label="Property condition" value={condition} onChange={(v) => setCondition(v as Condition)} options={conditionOpts.map((c) => c.label)} valueMap={conditionOpts.map((c) => c.id)} />
            <LabeledSelect label="Furnished?" value={furnished} onChange={(v) => setFurnished(v as typeof furnished)} options={["Furnished", "Unfurnished", "Partially furnished"]} valueMap={["furnished", "unfurnished", "partial"]} />
            <div className="grid grid-cols-2 gap-4">
              <LabeledSelect label="Pets?" value={pets} onChange={(v) => setPets(v as typeof pets)} options={["No", "Yes"]} valueMap={["no", "yes"]} />
              <LabeledSelect label="Parking?" value={parking} onChange={(v) => setParking(v as typeof parking)} options={["Yes", "No"]} valueMap={["yes", "no"]} />
            </div>
          </div>
        </Section>

        {/* Step 3: Frequency */}
        <Section step={3} title="How often">
          <div className="flex flex-wrap gap-2">
            {frequencyOpts.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFrequency(f.id)}
                aria-pressed={frequency === f.id}
                className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                  frequency === f.id
                    ? "border-primary bg-sage-soft text-primary"
                    : "border-border bg-background hover:border-primary"
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </Section>

        {/* Step 4: Add-ons */}
        <Section step={4} title="Optional add-ons">
          <div className="mb-4 flex items-center gap-2 rounded-xl border border-primary/20 bg-sage-soft/60 px-4 py-3 text-sm text-foreground/85">
            <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
            <span>
              {service === "tenancy" || service === "military"
                ? "Inside window and inside cupboard cleaning are included at no extra cost."
                : "Inside window cleaning is included in every quote at no extra cost."}
            </span>
          </div>
          <div className="flex flex-wrap gap-2">
            {ADDONS.filter((a) =>
              a.id !== "extra-bathroom" &&
              (service === "tenancy" || service === "military" ? a.id !== "cupboards" : true),
            ).map((a) => {
              const active = addons.includes(a.id);
              const suffix =
                "ptc" in a && a.ptc
                  ? "price to confirm"
                  : "pct" in a && a.pct
                    ? "+20%"
                    : a.amount
                      ? `+${formatPrice(a.amount)}${a.id === "wheelie-bins" ? " per bin" : ""}`
                      : "";
              return (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => toggleAddon(a.id)}
                  aria-pressed={active}
                  className={`rounded-full border px-3.5 py-1.5 text-sm transition-colors ${
                    active ? "border-primary bg-sage-soft text-primary" : "border-border bg-background hover:border-primary"
                  }`}
                >
                  {a.label}
                  {suffix && <span className="ml-2 text-xs text-muted-foreground">{suffix}</span>}
                </button>
              );
            })}
          </div>
          {addons.includes("wheelie-bins") && (
            <label className="mt-4 flex items-center gap-3 text-sm font-medium">
              <span>Number of wheelie bins</span>
              <Input aria-label="Number of wheelie bins" type="number" min="1" step="1" className="w-24" value={wheelieBins} onChange={(e) => setWheelieBins(Math.max(1, Math.floor(Number(e.target.value) || 1)))} />
            </label>
          )}
          {addons.includes("carpets") && (
            <div className="mt-4 rounded-2xl border border-border bg-sage-soft/40 p-5">
              <p className="text-sm font-semibold">Which carpets need cleaning?</p>
              <p className="mt-1 text-xs text-muted-foreground">Add each room with + / − — the total is added to your instant estimate.</p>
              <ul className="mt-3 grid gap-x-8 gap-y-2 text-sm sm:grid-cols-2">
                {CARPET_PRICES.map((c) => {
                  const qty = carpetRooms[c.id] ?? 0;
                  return (
                    <li key={c.id} className="flex items-center justify-between gap-3">
                      <span className="text-muted-foreground">
                        {c.label} <span className="text-xs">({formatPrice(c.price)})</span>
                      </span>
                      <span className="flex items-center gap-2">
                        <button
                          type="button"
                          aria-label={`Remove one ${c.label}`}
                          onClick={() => setCarpetQty(c.id, qty - 1)}
                          disabled={qty === 0}
                          className="h-7 w-7 rounded-full border border-border bg-background text-sm leading-none transition-colors hover:border-primary disabled:opacity-40"
                        >
                          −
                        </button>
                        <span className="w-5 text-center font-medium" aria-live="polite">{qty}</span>
                        <button
                          type="button"
                          aria-label={`Add one ${c.label}`}
                          onClick={() => setCarpetQty(c.id, qty + 1)}
                          className="h-7 w-7 rounded-full border border-border bg-background text-sm leading-none transition-colors hover:border-primary"
                        >
                          +
                        </button>
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-3 text-xs text-muted-foreground">Final price confirmed on the number and size of rooms.</p>
            </div>
          )}
        </Section>
        <Section step={5} title="Your details">
          <form onSubmit={onSubmit} noValidate className="grid gap-4 md:grid-cols-2">
            <input type="text" name="botcheck" tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: "absolute", left: "-9999px", opacity: 0 }} />
            <Field label="Full name" name="name" required placeholder="Jane Smith" error={fieldErrors.name} />
            <Field label="Phone" name="phone" type="tel" required placeholder="07763 933920" error={fieldErrors.phone} />
            <Field label="Email" name="email" type="email" required placeholder="you@example.com" error={fieldErrors.email} />
            <Field label="Postcode" name="postcode" required placeholder="SP9 7BB" error={fieldErrors.postcode} defaultValue={seed.postcode ?? ""} />
            <Field label="Address (optional)" name="address" placeholder="Street / area" error={fieldErrors.address} />
            <Field label="Preferred date" name="date" type="date" min={today} error={fieldErrors.date} defaultValue={seed.date ?? ""} />

            <label className="md:col-span-2 flex flex-col gap-1.5">
              <span className="text-sm font-medium text-foreground/85">Extra notes (access, pets, etc.)</span>
              <textarea
                name="notes"
                rows={3}
                maxLength={1500}
                className="rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
            </label>

            <label className="md:col-span-2 flex items-start gap-3 text-sm text-muted-foreground">
              <input type="checkbox" name="consent" className="mt-1 accent-primary" />
              <span>
                I'm happy for IZHUB Cleaning to contact me about this enquiry. See our{" "}
                <a href="/privacy" className="underline underline-offset-4">privacy policy</a>.
              </span>
            </label>
            {fieldErrors.consent && <p className="md:col-span-2 -mt-2 text-xs text-destructive">{fieldErrors.consent}</p>}

            {status === "error" && (
              <div className="md:col-span-2 rounded-2xl border border-destructive/30 bg-destructive/5 p-4 text-sm flex gap-3">
                <AlertCircle className="h-5 w-5 text-destructive shrink-0" />
                <p className="text-destructive">{errorMsg}</p>
              </div>
            )}

            <div className="md:col-span-2 flex flex-col sm:flex-row gap-3">
              <button type="submit" disabled={status === "sending"} className="btn-primary flex-1">
                <Send className="h-4 w-4" />
                {status === "sending" ? "Sending…" : "Submit quote request"}
              </button>
              <a href={whatsappHref} onClick={openWhatsAppWithDetails} target="_blank" rel="noreferrer" className="btn-whatsapp flex-1 justify-center">
                <MessageCircle className="h-4 w-4" /> WhatsApp this quote
              </a>
              <a href={`tel:${SITE.phone}`} className="btn-outline flex-1 justify-center">
                <Phone className="h-4 w-4" /> Call IZHUB
              </a>
            </div>
          </form>
        </Section>
      </div>

      {/* RIGHT — live estimate panel */}
      <aside className="lg:sticky lg:top-24 h-fit rounded-3xl border border-border bg-card p-6 md:p-8 shadow-soft">
        <p className="eyebrow">Your instant estimate</p>
        <div className="mt-4">
          {result.mode === "custom" ? (
            <>
              <p className="font-display text-3xl">Custom quote</p>
              <p className="mt-2 text-sm text-muted-foreground">
                Commercial cleaning is priced per site. We'll build your quote once we know the details.
              </p>
            </>
          ) : (
            <>
              <p className="font-display text-4xl md:text-5xl text-primary">{formatRange(result)}</p>
            </>
          )}
        </div>

        <div className="mt-5 flex items-center gap-2 rounded-xl bg-sage-soft/60 px-3 py-2.5 text-sm text-foreground/85">
          <CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />
          <span>
            {service === "tenancy" || service === "military"
              ? "Inside window and inside cupboard cleaning included"
              : "Inside window cleaning included"}
          </span>
        </div>

        {result.breakdown.length > 0 && (
          <div className="mt-6 border-t border-border pt-4">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Breakdown</p>
            <ul className="mt-2 space-y-1.5 text-sm">
              {result.breakdown.map((b, i) => (
                <li key={i} className="flex justify-between gap-3">
                  <span className="text-foreground/85">{b.label}</span>
                  <span className="text-muted-foreground">
                    {b.amount !== undefined ? formatPrice(Math.round(b.amount)) : b.note}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {result.priceToConfirm.length > 0 && (
          <div className="mt-4 rounded-xl border border-amber-300/40 bg-amber-50/60 p-3 text-xs text-amber-900">
            <p className="font-medium">Price to confirm:</p>
            <ul className="mt-1 list-disc pl-4 space-y-0.5">
              {result.priceToConfirm.map((p) => <li key={p}>{p}</li>)}
            </ul>
          </div>
        )}

        <div className="mt-6 flex gap-2 text-xs text-muted-foreground border-t border-border pt-4">
          <Info className="h-4 w-4 shrink-0 mt-0.5" />
          <p>{result.disclaimer}</p>
        </div>

        <div className="mt-5 flex flex-col gap-2">
          <a href={whatsappHref} onClick={openWhatsAppWithDetails} target="_blank" rel="noreferrer" className="btn-whatsapp w-full justify-center">
            <MessageCircle className="h-4 w-4" /> WhatsApp this quote
          </a>
          <a href={`tel:${SITE.phone}`} className="btn-outline w-full justify-center">
            <Phone className="h-4 w-4" /> Call {SITE.phoneDisplay}
          </a>
        </div>
      </aside>
    </div>
  );
}

function PrefillRow({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between gap-3 border-b border-primary/10 py-1 last:border-0">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">{k}</dt>
      <dd className="text-sm font-medium text-foreground text-right">{v}</dd>
    </div>
  );
}

function Section({ step, title, children }: { step: number; title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-3xl border border-border bg-card p-6 md:p-8 shadow-soft">
      <div className="flex items-center gap-3">
        <span className="grid h-8 w-8 place-items-center rounded-full bg-primary text-primary-foreground text-sm font-medium">
          {step}
        </span>
        <h2 className="font-display text-xl md:text-2xl">{title}</h2>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}

function LabeledSelect({
  label,
  value,
  onChange,
  options,
  valueMap,
  labelize,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: readonly string[];
  valueMap?: readonly string[];
  labelize?: boolean;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground/85">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="rounded-xl border border-input bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {options.map((o, i) => {
          const v = valueMap ? valueMap[i] : o;
          const display = labelize ? o.charAt(0).toUpperCase() + o.slice(1) : o;
          return (
            <option key={v} value={v}>
              {display}
            </option>
          );
        })}
      </select>
    </label>
  );
}

function Field({
  label,
  name,
  type = "text",
  required,
  placeholder,
  min,
  error,
  defaultValue,
}: {
  label: string;
  name: string;
  type?: string;
  required?: boolean;
  placeholder?: string;
  min?: string;
  error?: string;
  defaultValue?: string;
}) {
  return (
    <label className="flex flex-col gap-1.5">
      <span className="text-sm font-medium text-foreground/85">
        {label}
        {required && <span className="text-primary"> *</span>}
      </span>
      <input
        name={name}
        type={type}
        required={required}
        placeholder={placeholder}
        min={min}
        defaultValue={defaultValue}
        aria-invalid={!!error}
        className={`rounded-xl border bg-background px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring ${error ? "border-destructive" : "border-input"}`}
      />
      {error && <span className="text-xs text-destructive">{error}</span>}
    </label>
  );
}
